import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LuArrowRight, LuClock, LuFileText, LuPlus, LuSquareCheckBig, LuWallet } from "react-icons/lu";
import { Loader } from "../components/ui/Loader";
import { ProjectsGrid } from "../components/shared/ProjectsGrid";
import {
  DashboardStats,
  fetchDashboardStats,
  fetchOwnedProjects,
} from "../lib/projects";
import { fetchAllProjects } from "../lib/admin";
import {
  formatMoney,
  formatMonthYear,
  formatRelativeDate,
} from "../lib/projects";
import {
  NOTIFICATION_META,
  activityFromProjects,
  fetchNotifications,
} from "../lib/notifications";
import { AppNotification } from "../models/notification";
import { useAuth } from "../lib/auth";
import { Project } from "../models/project";

export const Main = () => {
  const { profile } = useAuth();
  const isStaff = profile?.role === "admin" || profile?.role === "manager";
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile?.role) return;
    Promise.all([
      fetchDashboardStats(),
      isStaff ? fetchAllProjects(5) : fetchOwnedProjects(5),
      fetchNotifications(3).catch(() => [] as AppNotification[]),
    ])
      .then(async ([s, p, n]) => {
        setStats(s);
        setProjects(p);
        setNotifications(n);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Помилка завантаження"))
      .finally(() => setLoading(false));
  }, [isStaff, profile?.role]);

  const feed: AppNotification[] = useMemo(
    () =>
      notifications.length > 0
        ? notifications
        : activityFromProjects(projects, 3, isStaff ? "/admin/projects" : "/projects"),
    [notifications, projects, isStaff]
  );

  const statCards = stats
    ? [
        {
          icon: LuFileText,
          tint: "text-sky-400 bg-sky-500/10",
          value: String(stats.total),
          label: "Проєктів",
          sub: "Всього",
        },
        {
          icon: LuClock,
          tint: "text-brand bg-brand/10",
          value: String(stats.inProgress),
          label: "В роботі",
          sub: "Зараз",
        },
        {
          icon: LuSquareCheckBig,
          tint: "text-green-400 bg-green-500/10",
          value: String(stats.completed),
          label: "Реалізовано",
          sub: "Завершено",
        },
        {
          icon: LuWallet,
          tint: "text-purple-400 bg-purple-500/10",
          value: formatMoney(stats.bonusTotal),
          label: "Бонуси",
          sub: isStaff ? "Всі партнери" : "Отримано",
        },
      ]
    : [];

  return (
    <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-5 sm:px-5 sm:py-6 md:px-8 md:py-8">
      {loading ? (
        <Loader />
      ) : error ? (
            <p className="rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                {statCards.map(({ icon: Icon, tint, value, label, sub }) => (
                  <article
                    key={label}
                    className="card py-2.5 px-3 sm:p-4 relative flex min-w-0 items-start gap-x-2 sm:items-center sm:gap-x-3"
                  >
                    <span
                      className={`flex shrink-0 items-center justify-center sm:h-12 sm:w-12 rounded sm:rounded-lg ${tint}`}
                    >
                      <Icon className="sm:text-xl" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs leading-4 text-neutral-300 sm:text-sm">
                        {label}
                      </p>
                      <p className="mt-0.5 font-bold text-white sm:hidden">
                        {value}
                      </p>
                      <p className="sm:mt-1 text-xs text-neutral-500 hidden sm:block">{sub}</p>
                    </div>
                    <p className="z-10 hidden truncate text-lg font-bold text-white sm:block sm:text-2xl sm:ml-auto">
                      {value}
                    </p>
                  </article>
                ))}
              </div>

              <div className="mt-5 sm:mt-7 items-start grid grid-cols-1 gap-5 sm:gap-6 xl:grid-cols-3">
                <div className="xl:col-span-2">
                  {!isStaff && (
                    <Link
                      to="/submit"
                      className="mb-5 md:hidden btn-brand w-full justify-center px-5 py-3 text-xs whitespace-nowrap"
                    >
                      <LuPlus className="text-xl shrink-0" aria-hidden />
                      Передати новий проєкт
                    </Link>
                  )}
                  <section className="card p-4 sm:p-6 min-h-full">
                    <div className="flex items-center justify-between">
                      <h2 className={`text-xl font-bold text-white${projects.length === 0 ? " hidden sm:block" : ""}`}>Останні проєкти</h2>
                      {isStaff ? (
                        <Link
                          to="/admin/projects"
                          className="text-sm font-medium text-brand transition hover:opacity-80 whitespace-nowrap"
                        >
                          Всі проєкти
                        </Link>
                      ) : (
                        <Link
                          to="/submit"
                          className="hidden md:flex btn-brand px-5 py-3 text-xs whitespace-nowrap"
                        >
                          <LuPlus className="text-xl shrink-0" aria-hidden />
                          Передати новий проєкт
                        </Link>
                      )}
                    </div>
                    {projects.length === 0 ? (
                      <div className="flex flex-col gap-2 items-center justify-center sm:card sm:mt-7 px-10 py-3 sm:p-8 lg:min-h-[250px] text-center">
                        <p className="text-lg font-semibold text-white">Проєктів ще немає</p>
                        <p className="text-sm text-neutral-400">
                          Передайте перший проєкт, щоб розпочати співпрацю
                        </p>
                      </div>
                    ) : (
                      <div className="mt-5 sm:mt-7">
                        <ProjectsGrid projects={projects} showOwner={isStaff} hrefBase={isStaff ? "/admin/projects" : "/projects"} />
                      </div>
                    )}
                    {!isStaff && projects.length > 0 && (
                      <Link
                        to="/projects"
                        className="mt-5 flex items-center justify-center gap-2 text-sm font-medium text-brand transition hover:opacity-80"
                      >
                        Дивитись всі проєкти
                        <LuArrowRight className="text-base" aria-hidden />
                      </Link>
                    )}
                  </section>
                </div>

                <aside className="flex flex-col gap-5">
                  <section className="card hidden p-4 sm:p-6 md:block">
                    <div className="mb-5 flex items-center justify-between gap-4">
                      <h2 className="text-lg font-bold text-white">
                        Останні сповіщення
                      </h2>
                      <Link
                        to="/notifications"
                        className="text-sm font-medium text-brand transition hover:opacity-80"
                      >
                        Всі
                      </Link>
                    </div>
                    {feed.length === 0 ? (
                      <p className="text-sm text-neutral-400">
                        Сповіщень ще немає
                      </p>
                    ) : (
                      <ul>
                        {feed.map((item, idx) => {
                          const meta = NOTIFICATION_META[item.type];
                          const Icon = meta.icon;
                          const content = (
                            <>
                              <span
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${meta.tint}`}
                              >
                                <Icon className="text-lg" aria-hidden />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="flex items-center justify-between gap-3">
                                  <span className="truncate text-sm font-semibold text-white">
                                    {item.title}
                                  </span>
                                  <span className="shrink-0 text-xs text-neutral-500">
                                    {formatRelativeDate(item.created_at)}
                                  </span>
                                </span>
                                {item.body && (
                                  <span className="mt-0.5 block text-xs text-neutral-400">
                                    {item.body}
                                  </span>
                                )}
                              </span>
                            </>
                          );
                          return (
                            <li
                              key={`${item.type}-${item.id}`}
                              className={`py-3${idx > 0 ? " border-t border-line" : " pt-0"}${idx === feed.length - 1 ? " pb-0" : ""}`}
                            >
                              {item.link ? (
                                <Link
                                  to={item.link}
                                  className="flex items-start gap-3 hover:opacity-90"
                                >
                                  {content}
                                </Link>
                              ) : (
                                <div className="flex items-start gap-3">{content}</div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>

                  <section className="card p-4 sm:p-6">
                    <h2 className="mb-2 text-lg font-bold text-white">
                      Ваша статистика
                    </h2>
                    <dl className="divide-y divide-line">
                      <div className="flex items-center justify-between gap-4 py-2 sm:py-3">
                        <dt className="text-sm text-neutral-400">
                          Середній бонус з проєкту
                        </dt>
                        <dd className="text-sm font-bold text-brand">
                          {formatMoney(stats?.avgBonus ?? 0)}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-2 sm:py-3">
                        <dt className="text-sm text-neutral-400">
                          Загальна сума договорів
                        </dt>
                        <dd className="text-sm font-bold text-white">
                          {formatMoney(stats?.contractTotal ?? 0)}
                        </dd>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-2 sm:py-3">
                        <dt className="text-sm text-neutral-400">Партнер з</dt>
                        <dd className="text-sm font-semibold text-white">
                          {profile?.created_at ? formatMonthYear(profile.created_at) : "—"}
                        </dd>
                      </div>
                    </dl>
                  </section>
                </aside>
              </div>
            </>
          )}
    </main>
  );
};
